/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC } from "react";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Chip, IconButton } from "@mui/material";
import Link from "next/link";

import { MyTripsBusinessCase } from "@fllite/ui/types";
import {
  formatDateWithTz,
  getTripStatusColor,
  translateBusinessCaseStatusWithIcon,
} from "@fllite/ui/utils";

import {
  ActionCell,
  BodyCell,
  BodyRow,
  CellValue,
  ColumnRowLegCount,
  ColumnRowTitle,
  StatusCell,
  StatusChips,
} from "./styles";

interface BusinessCaseRowProps {
  businessCase: MyTripsBusinessCase;
  metaDate: string | null | undefined;
  actionNeeded?: boolean;
}

export const BusinessCaseRow: FC<BusinessCaseRowProps> = ({
  businessCase,
  metaDate,
  actionNeeded = false,
}) => {
  const inquiryLegs = businessCase.inquiry?.inquiryLegs ?? [];
  const firstLeg = inquiryLegs[0];
  const departureCode = firstLeg?.departureAirport?.airportCode ?? "—";
  const destinationCode = firstLeg?.destinationAirport?.airportCode ?? "—";
  const legCount = inquiryLegs.length;
  const timeZone = firstLeg?.departureAirport?.timeZone;
  const formattedMeta =
    metaDate && timeZone ? formatDateWithTz(metaDate, timeZone, "LL") : "—";

  const user = businessCase.user;
  const customerName =
    user && (user.firstName || user.lastName)
      ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
      : "—";

  const tailNumber =
    businessCase.trip?.proposal?.proposalLegs?.[0]?.charterOperatorAircraft
      ?.tailNumber ?? "—";

  return (
    <BodyRow>
      <BodyCell>
        <ColumnRowTitle>
          {departureCode}
          <ArrowRightAltIcon fontSize="small" />
          {destinationCode}
          {legCount > 1 ? (
            <ColumnRowLegCount>({legCount} legs)</ColumnRowLegCount>
          ) : null}
          {businessCase.tripNumber ? (
            <Chip
              label={`#${businessCase.tripNumber}`}
              size="small"
              variant="outlined"
              sx={{ ml: 1, height: 20 }}
            />
          ) : null}
        </ColumnRowTitle>
      </BodyCell>
      <BodyCell>
        <CellValue title={customerName}>{customerName}</CellValue>
      </BodyCell>
      <BodyCell>
        <CellValue title={tailNumber}>{tailNumber}</CellValue>
      </BodyCell>
      <BodyCell>{formattedMeta}</BodyCell>
      <StatusCell>
        <StatusChips>
          {actionNeeded ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<WarningAmberRoundedIcon style={{ fontSize: 14 }} />}
              label="Action needed"
              sx={{ height: 24 }}
            />
          ) : null}
          {businessCase.status ? (
            <Chip
              size="small"
              color={
                getTripStatusColor(
                  businessCase.status,
                  businessCase.contractSignStatus,
                ) as any
              }
              label={translateBusinessCaseStatusWithIcon(businessCase)}
              sx={{ height: 24 }}
            />
          ) : null}
        </StatusChips>
      </StatusCell>
      <ActionCell>
        <Link href={`/trip/${businessCase.id}`} legacyBehavior>
          <IconButton aria-label="Open trip">
            <NavigateNextIcon />
          </IconButton>
        </Link>
      </ActionCell>
    </BodyRow>
  );
};
